import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Select from "react-select";
import { Gavel, Send, Trophy, X, Plus, RefreshCw, CheckCircle2 } from "lucide-react";
import ConfirmationModal from "../../components/ConfirmationModal";
import AsyncButton from "../../components/AsyncButton";
import { getBoqSkuMapping } from "../../services/interiorExecution";
import {
  createTender,
  listStageTenders,
  getTenderVendorMatches,
  setTenderVendors,
  sendTender,
  getTenderComparison,
  awardTenderLine,
  finalizeTender,
  shortlistTender,
  cancelTender,
} from "../../services/interiorTender";

/**
 * Per-stage (per-package) tender management. BV floats ONE consolidated tender covering all mapped
 * items in the stage and invites vendors; each vendor gets a single magic link showing only the
 * items its sub-category coverage supports. Quotes are compared per item across vendors and a
 * winner is awarded PER ITEM. Winning lines feed the PO panel + the stage's materials cost.
 */
const STATUS_STYLE = {
  DRAFT: "bg-slate-100 text-slate-600 border-slate-200",
  SENT: "bg-sky-50 text-sky-700 border-sky-200",
  OPEN: "bg-sky-50 text-sky-700 border-sky-200",
  SHORTLISTED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  CANCELLED: "bg-rose-50 text-rose-600 border-rose-200",
};

const TenderPanel = ({ leadId, stage, onChanged }) => {
  const stageId = stage?.idExecutionPlanStageId;
  const stageKey = stage?.stageKey;
  // Move-in Service is a single-price SERVICE tender (no BOQ items) — a different flow.
  const isMoveIn = stageKey === "MOVE_IN_SERVICE";
  const [items, setItems] = useState([]); // mapped BOQ items for this package (material only)
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [inviteFor, setInviteFor] = useState(null); // tender being invited
  const [vendorOptions, setVendorOptions] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [showAllVendors, setShowAllVendors] = useState(false); // move-in: bypass local/intercity gate
  const [compareFor, setCompareFor] = useState(null); // material: { tender, comparison }
  const [serviceCompareFor, setServiceCompareFor] = useState(null); // move-in: { tender, comparison }
  const [cancelId, setCancelId] = useState(null);

  const load = useCallback(async () => {
    if (!stageId) return;
    setLoading(true);
    try {
      const stageTenders = await listStageTenders(stageId);
      setTenders(Array.isArray(stageTenders) ? stageTenders : []);
      if (isMoveIn) {
        setItems([]); // move-in has no BOQ items
      } else {
        const mapping = await getBoqSkuMapping(leadId);
        const group = (mapping?.groups || []).find((g) => g.packageKey === stageKey);
        setItems((group?.lines || []).filter((l) => l.isMapped));
      }
    } catch (e) {
      setItems([]);
      setTenders([]);
    } finally {
      setLoading(false);
    }
  }, [leadId, stageId, stageKey, isMoveIn]);

  useEffect(() => {
    load();
  }, [load]);

  // BOQ ids already covered by a live (non-cancelled) tender.
  const tenderedIds = new Set(
    tenders
      .filter((t) => t.status !== "CANCELLED")
      .flatMap((t) => (t.lines || []).map((l) => l.interiorDesignerBoqId))
  );
  const untendered = items.filter((l) => !tenderedIds.has(l.interiorDesignerBoqId));

  // One consolidated tender for ALL untendered mapped items in this stage.
  const floatStage = async () => {
    if (untendered.length === 0) return;
    setBusy(true);
    try {
      await createTender({
        planStageId: stageId,
        title: stage?.stageLabel || stage?.stageKey || "Stage tender",
        interiorDesignerBoqIds: untendered.map((l) => l.interiorDesignerBoqId),
      });
      toast.success("Consolidated tender created — invite vendors next");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create tender");
    } finally {
      setBusy(false);
    }
  };

  // Move-in: one single-price SERVICE tender for this stage.
  const floatMoveIn = async () => {
    setBusy(true);
    try {
      await createTender({
        planStageId: stageId,
        tenderType: "SERVICE",
        title: stage?.stageLabel || "Move-in Service",
      });
      toast.success("Move-in tender created — invite vendors next");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create move-in tender");
    } finally {
      setBusy(false);
    }
  };

  const vendorLabel = (m) => {
    if (!isMoveIn) return m.vendorName;
    const bits = [];
    if (m.servesLocal) bits.push("Local");
    if (m.servesIntercity) bits.push("Intercity");
    if (m.baseCity) bits.push(m.baseCity);
    return bits.length ? `${m.vendorName} — ${bits.join(" · ")}` : m.vendorName;
  };

  const fetchVendorMatches = useCallback(async (tenderId, all) => {
    try {
      const matches = await getTenderVendorMatches(tenderId, all);
      setVendorOptions((matches || []).map((m) => ({ value: m.vendorId, label: vendorLabel(m) })));
    } catch {
      setVendorOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMoveIn]);

  const openInvite = async (tender) => {
    setInviteFor(tender);
    setShowAllVendors(false);
    setSelectedVendors((tender.vendors || []).map((v) => ({ value: v.vendorId, label: v.vendorName })));
    await fetchVendorMatches(tender.tenderId, false);
  };

  const toggleShowAll = async (next) => {
    setShowAllVendors(next);
    if (inviteFor) await fetchVendorMatches(inviteFor.tenderId, next);
  };

  const openServiceCompare = async (tender) => {
    try {
      const comparison = await getTenderComparison(tender.tenderId);
      setServiceCompareFor({ tender, comparison });
    } catch (e) {
      toast.error("Failed to load quotes");
    }
  };

  const awardService = async (tenderQuoteId) => {
    setBusy(true);
    try {
      await shortlistTender(serviceCompareFor.tender.tenderId, tenderQuoteId);
      toast.success("Vendor awarded — price added to the move-in package");
      setServiceCompareFor(null);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to award vendor");
    } finally {
      setBusy(false);
    }
  };

  const saveInvite = async () => {
    setBusy(true);
    try {
      await setTenderVendors(inviteFor.tenderId, selectedVendors.map((v) => v.value));
      toast.success("Vendors updated");
      setInviteFor(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to set vendors");
    } finally {
      setBusy(false);
    }
  };

  const doSend = async (tender) => {
    setBusy(true);
    try {
      await sendTender(tender.tenderId, {});
      toast.success("Magic links sent to vendors");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send tender");
    } finally {
      setBusy(false);
    }
  };

  const openCompare = async (tender) => {
    try {
      const comparison = await getTenderComparison(tender.tenderId);
      setCompareFor({ tender, comparison });
    } catch (e) {
      toast.error("Failed to load quotes");
    }
  };

  const awardLine = async (tenderQuoteLineId) => {
    setBusy(true);
    try {
      const comparison = await awardTenderLine(compareFor.tender.tenderId, tenderQuoteLineId);
      setCompareFor((prev) => ({ ...prev, comparison }));
      toast.success("Winner set for this item");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to award item");
    } finally {
      setBusy(false);
    }
  };

  const finalize = async () => {
    setBusy(true);
    try {
      await finalizeTender(compareFor.tender.tenderId);
      toast.success("Tender finalized — winning prices added to the stage");
      setCompareFor(null);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to finalize tender");
    } finally {
      setBusy(false);
    }
  };

  const doCancel = async () => {
    const id = cancelId;
    setCancelId(null);
    try {
      await cancelTender(id);
      toast.success("Tender cancelled");
      await load();
    } catch (e) {
      toast.error("Failed to cancel tender");
    }
  };

  if (!stageId) return null;

  // Per-item comparison helpers for the award modal.
  const comp = compareFor?.comparison;
  const quoteLinesForItem = (boqId) =>
    (comp?.quotes || []).flatMap((q) =>
      (q.lines || [])
        .filter((ql) => ql.interiorDesignerBoqId === boqId)
        .map((ql) => ({ ...ql, vendorName: q.vendorName }))
    );
  const itemHasWinner = (boqId) => quoteLinesForItem(boqId).some((ql) => ql.isWinning);
  const allItemsAwarded =
    !!comp && (comp.lines || []).length > 0 && (comp.lines || []).every((l) => itemHasWinner(l.interiorDesignerBoqId));

  const renderTenderRow = (t) => {
    const itemCount = (t.lines || []).length;
    const submitted = (t.vendors || []).filter((v) => v.hasQuote).length;
    return (
      <div key={t.tenderId} className="border border-slate-200 rounded-lg px-3 py-2 mb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-800 truncate flex-1">
            {t.title || `Tender #${t.tenderId}`}
            {!isMoveIn && (
              <span className="text-slate-400 font-normal"> · {itemCount} item{itemCount === 1 ? "" : "s"}</span>
            )}
          </span>
          <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold border ${STATUS_STYLE[t.status] || STATUS_STYLE.DRAFT}`}>
            {t.status}
          </span>
        </div>
        <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500">
          <span>{(t.vendors || []).length} vendor(s)</span>
          <span>{submitted} quoted</span>
          <div className="ml-auto flex items-center gap-1.5">
            {t.status === "DRAFT" && (
              <>
                <button onClick={() => openInvite(t)} className="text-flamingo-700 hover:underline font-medium">Invite</button>
                {(t.vendors || []).length > 0 && (
                  <AsyncButton onClick={() => doSend(t)} disabled={busy} loadingText="Sending" className="inline-flex items-center justify-center gap-1 text-sky-700 hover:underline font-medium">
                    <Send size={11} /> Send
                  </AsyncButton>
                )}
              </>
            )}
            {(t.status === "SENT" || t.status === "OPEN") && (
              <button onClick={() => openInvite(t)} className="text-flamingo-700 hover:underline font-medium">Vendors</button>
            )}
            {submitted > 0 && t.status !== "SHORTLISTED" && (
              <button onClick={() => (isMoveIn ? openServiceCompare(t) : openCompare(t))} className="inline-flex items-center gap-1 text-emerald-700 hover:underline font-medium">
                <Trophy size={11} /> Compare & award
              </button>
            )}
            {t.status === "SHORTLISTED" && <span className="text-emerald-600 font-medium">Awarded</span>}
            {t.status !== "SHORTLISTED" && (
              <button onClick={() => setCancelId(t.tenderId)} className="text-rose-500 hover:underline">Cancel</button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const activeTenders = tenders.filter((t) => t.status !== "CANCELLED");

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 mb-3">
      <div className="flex items-center gap-2 mb-3">
        <Gavel size={16} className="text-flamingo-600" />
        <span className="text-sm font-semibold text-slate-800">{isMoveIn ? "Move-in tender" : "Tenders"}</span>
        <span className="text-xs text-slate-400">
          {isMoveIn
            ? "invite move-in vendors · they quote one price · award the best"
            : "one consolidated tender · vendors quote only their items · award per item"}
        </span>
        <button onClick={load} className="ml-auto text-slate-400 hover:text-slate-600" title="Refresh">
          <RefreshCw size={14} />
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : isMoveIn ? (
        <>
          {/* Move-in: float one single-price SERVICE tender when none is active */}
          {activeTenders.length === 0 && (
            <div className="mb-3 rounded-lg border border-flamingo-100 bg-flamingo-50/40 p-3 flex items-center justify-between gap-2">
              <p className="text-xs font-medium text-slate-600">
                Configure the move-in details, then float a tender to move-in vendors.
              </p>
              <AsyncButton
                onClick={floatMoveIn}
                disabled={busy}
                loadingText="Floating"
                className="inline-flex items-center justify-center gap-1 rounded-lg bg-flamingo-600 hover:bg-flamingo-700 disabled:opacity-60 text-white text-xs font-semibold px-3 py-1.5"
              >
                <Plus size={12} /> Float move-in tender
              </AsyncButton>
            </div>
          )}
          {activeTenders.map(renderTenderRow)}
        </>
      ) : items.length === 0 ? (
        <p className="text-xs text-slate-500">
          No mapped items for this package yet. Assign category/brand in Material SKU Mapping first.
        </p>
      ) : (
        <>
          {/* Untendered mapped items → float ONE consolidated tender */}
          {untendered.length > 0 && (
            <div className="mb-3 rounded-lg border border-flamingo-100 bg-flamingo-50/40 p-3">
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <p className="text-xs font-medium text-slate-600">
                  {untendered.length} item{untendered.length === 1 ? "" : "s"} ready to tender
                </p>
                <AsyncButton
                  onClick={floatStage}
                  disabled={busy}
                  loadingText="Floating"
                  className="inline-flex items-center justify-center gap-1 rounded-lg bg-flamingo-600 hover:bg-flamingo-700 disabled:opacity-60 text-white text-xs font-semibold px-3 py-1.5"
                >
                  <Plus size={12} /> Float tender ({untendered.length})
                </AsyncButton>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {untendered.map((l) => (
                  <span key={l.interiorDesignerBoqId} className="inline-flex items-center rounded-full bg-white border border-slate-200 px-2 py-0.5 text-[11px] text-slate-600">
                    {l.material}
                    {l.materialSubcategory ? ` · ${l.materialSubcategory}` : ""}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Existing tenders */}
          {activeTenders.map(renderTenderRow)}
        </>
      )}

      {/* Invite vendors modal */}
      {inviteFor && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h3 className="text-base font-semibold text-slate-900">Invite Vendors</h3>
              <button onClick={() => setInviteFor(null)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <div className="p-5">
              <p className="text-xs text-slate-500 mb-2">
                {isMoveIn
                  ? "Move-in vendors matching this move (local/intercity). Toggle to see every move-in vendor."
                  : "Suggested vendors cover at least one of this tender's items. Each vendor's link shows only the items they can supply."}
              </p>
              {isMoveIn && (
                <label className="flex items-center gap-2 text-xs text-slate-600 mb-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={showAllVendors}
                    onChange={(e) => toggleShowAll(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-slate-300 text-flamingo-600 focus:ring-flamingo-500/40"
                  />
                  Show all move-in vendors (ignore local/intercity match)
                </label>
              )}
              <Select
                isMulti
                options={vendorOptions}
                value={selectedVendors}
                onChange={(opts) => setSelectedVendors(opts || [])}
                placeholder="Select vendors to invite"
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 bg-slate-50 border-t border-slate-200">
              <button onClick={() => setInviteFor(null)} className="rounded-xl border border-slate-300 text-slate-600 text-sm font-medium px-4 py-2">Cancel</button>
              <button onClick={saveInvite} disabled={busy} className="rounded-xl bg-flamingo-600 hover:bg-flamingo-700 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Compare & award (per item) modal */}
      {compareFor && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 sticky top-0 bg-white">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Compare & award — per item</h3>
                <p className="text-xs text-slate-500">Pick the winning vendor for each item, then finalize.</p>
              </div>
              <button onClick={() => setCompareFor(null)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              {(comp?.lines || []).length === 0 ? (
                <p className="text-sm text-slate-500">No items in this tender.</p>
              ) : (
                (comp.lines || []).map((line) => {
                  const qls = quoteLinesForItem(line.interiorDesignerBoqId);
                  return (
                    <div key={line.interiorDesignerBoqId} className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-100">
                        <span className="text-sm font-semibold text-slate-800">{line.material || "Item"}</span>
                        <span className="text-xs text-slate-400">
                          {[line.materialSubcategory, line.room, line.workItem].filter(Boolean).join(" · ")}
                          {line.quantity != null ? ` · qty ${line.quantity}${line.unit ? ` ${line.unit}` : ""}` : ""}
                        </span>
                        {itemHasWinner(line.interiorDesignerBoqId) && (
                          <CheckCircle2 size={14} className="text-emerald-500 ml-auto" />
                        )}
                      </div>
                      {qls.length === 0 ? (
                        <p className="px-3 py-2 text-xs text-slate-400">No vendor has quoted this item yet.</p>
                      ) : (
                        <div className="divide-y divide-slate-100">
                          {qls.map((ql) => (
                            <div key={ql.tenderQuoteLineId} className="flex items-center gap-3 px-3 py-2">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-slate-900">{ql.vendorName}{ql.brandName ? <span className="text-slate-400"> · {ql.brandName}</span> : null}</p>
                                <p className="text-xs text-slate-500">
                                  {ql.unitPrice != null ? `₹${ql.unitPrice}/unit` : "—"}
                                  {ql.lineTotal != null ? ` · total ₹${ql.lineTotal}` : ""}
                                  {ql.leadTimeDays != null ? ` · ${ql.leadTimeDays}d lead` : ""}
                                  {ql.availability ? ` · ${ql.availability}` : ""}
                                </p>
                              </div>
                              <AsyncButton
                                onClick={() => awardLine(ql.tenderQuoteLineId)}
                                disabled={busy || ql.isWinning}
                                loadingText="Awarding"
                                className={`inline-flex items-center justify-center gap-1 rounded-lg text-xs font-semibold px-3 py-1.5 ${
                                  ql.isWinning
                                    ? "bg-emerald-100 text-emerald-700 cursor-default"
                                    : "bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white"
                                }`}
                              >
                                <Trophy size={13} /> {ql.isWinning ? "Winner" : "Award"}
                              </AsyncButton>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            <div className="flex items-center justify-between gap-2 px-5 py-4 bg-slate-50 border-t border-slate-200 sticky bottom-0">
              <p className="text-xs text-slate-500">
                {allItemsAwarded ? "All items have a winner." : "Award a winner for every item to finalize."}
              </p>
              <AsyncButton
                onClick={finalize}
                disabled={busy || !allItemsAwarded}
                loadingText="Finalizing"
                className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-flamingo-600 hover:bg-flamingo-700 disabled:opacity-50 text-white text-sm font-semibold px-5 py-2"
              >
                <Trophy size={15} /> Finalize awards
              </AsyncButton>
            </div>
          </div>
        </div>
      )}

      {/* Move-in compare & award (single price) modal */}
      {serviceCompareFor && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 sticky top-0 bg-white">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Compare & award — move-in</h3>
                <p className="text-xs text-slate-500">Each vendor quoted one total price. Award the best.</p>
              </div>
              <button onClick={() => setServiceCompareFor(null)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-2">
              {(serviceCompareFor.comparison?.quotes || []).length === 0 ? (
                <p className="text-sm text-slate-500">No vendor has quoted yet.</p>
              ) : (
                (serviceCompareFor.comparison.quotes || []).map((q) => (
                  <div key={q.tenderQuoteId} className="flex items-center gap-3 border border-slate-200 rounded-xl px-3 py-2.5">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">{q.vendorName}</p>
                      <p className="text-xs text-slate-500">
                        {q.totalAmount != null ? `₹${Number(q.totalAmount).toLocaleString("en-IN")}` : "—"}
                        {q.leadTimeDays != null ? ` · ${q.leadTimeDays}d lead` : ""}
                        {q.overallNotes ? ` · ${q.overallNotes}` : ""}
                      </p>
                    </div>
                    <AsyncButton
                      onClick={() => awardService(q.tenderQuoteId)}
                      disabled={busy || q.isWinning}
                      loadingText="Awarding"
                      className={`inline-flex items-center justify-center gap-1 rounded-lg text-xs font-semibold px-3 py-1.5 ${
                        q.isWinning
                          ? "bg-emerald-100 text-emerald-700 cursor-default"
                          : "bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white"
                      }`}
                    >
                      <Trophy size={13} /> {q.isWinning ? "Winner" : "Award"}
                    </AsyncButton>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={!!cancelId}
        onConfirm={doCancel}
        onCancel={() => setCancelId(null)}
        message="Cancel this tender? Invited vendors will no longer be able to quote."
      />
    </div>
  );
};

export default TenderPanel;
